import { useCallback, useRef } from 'react';
import createClient from '@eid-easy/eideasy-browser-client';
import * as Sentry from '@sentry/react';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showBulkSignSkipNotices } from './bulkSignSkipNotices';
import {
	ensureError,
	isEidEasySandbox,
	isUserInitiatedError,
	toErrorKey,
} from './useEidEasySign.utils';
import type { BulkSignSelection } from './useBulkEidEasySign';

interface EidEasyBulkPrepareResponse {
	payload: {
		docId: string;
		clientId: string;
		baseUrl: string;
		mockMode?: boolean;
		skippedInstanceIds?: string[];
		witnessSkippedInstanceIds?: string[];
		deferredInstanceIds?: string[];
	};
}

interface UseBulkDrawSignOptions {
	basePath: string;
	onStatusChange: (status: EidEasySignStatus) => void;
	onError: (error: string) => void;
	onComplete: () => void;
}

/**
 * Bulk simple sign (handwritten-simple-signature).
 *
 * The user draws once; a single eID Easy widget session signs the whole batch
 * (no OTP). Mirrors the bulk-OTP direct flow: bulk-prepare embeds our stamp PNG
 * via the custom-signature-image webhook, the handwritten SDK launcher signs the
 * batch doc_id, then bulk-complete downloads and saves every signed PDF.
 */
export const useBulkDrawSign = ({
	basePath,
	onStatusChange,
	onError,
	onComplete,
}: UseBulkDrawSignOptions) => {
	const tMipManage = useTranslationMipManage();
	const docIdRef = useRef<string | null>(null);

	const startDraw = useCallback(
		async (
			signatureDataUrl: string,
			source: DrawnSignatureSource,
			selection: BulkSignSelection
		) => {
			onStatusChange(EidEasySignStatus.PREPARING);
			onError('');

			try {
				const prepareResult = await makeRequest<EidEasyBulkPrepareResponse>({
					endpoint: `${basePath}/eid-easy/bulk-prepare`,
					method: HTTP_METHOD.POST,
					body: JSON.stringify({
						...selection,
						method: EidEasySigningMethod.DRAW,
						drawnSignatureBase64: signatureDataUrl,
						drawnSignatureSource: source,
					}),
				});

				const { docId, clientId, baseUrl, mockMode } = prepareResult.payload;
				docIdRef.current = docId;

				showBulkSignSkipNotices(prepareResult.payload, tMipManage);

				if (!mockMode) {
					const client = createClient({
						clientId,
						docId,
						language: 'en',
						sandbox: isEidEasySandbox(baseUrl),
					});

					onStatusChange(EidEasySignStatus.AWAITING_SIGN);
					await new Promise<void>((resolve, reject) => {
						client.signature.handwrittenSignature.start({
							handwrittenSignatureDataUrl: signatureDataUrl,
							success: () => resolve(),
							fail: (failError: unknown) => reject(ensureError(failError)),
						});
					});
				}

				onStatusChange(EidEasySignStatus.COMPLETING);
				await makeRequest({
					endpoint: `${basePath}/eid-easy/bulk-complete`,
					method: HTTP_METHOD.POST,
					body: JSON.stringify({ docId }),
				});

				docIdRef.current = null;
				onStatusChange(EidEasySignStatus.DONE);
				onComplete();
			} catch (err: unknown) {
				if (!isUserInitiatedError(err)) {
					Sentry.captureException(err, {
						tags: { signingFlow: 'bulk-draw', phase: 'prepare' },
					});
				}
				onError(toErrorKey(err));
				onStatusChange(EidEasySignStatus.ERROR);
			}
		},
		[basePath, onStatusChange, onError, onComplete, tMipManage]
	);

	const reset = useCallback(() => {
		docIdRef.current = null;
	}, []);

	return { startDraw, reset };
};
